import { measureGeneration } from '~/server/static-metrics'
import { GetStaticProps, NextPage } from 'next'
import { useState } from 'react'
import { FaHistory } from 'react-icons/fa'
import InfiniteScroll from 'react-infinite-scroller'
import { BookList } from 'web_core/assets/book/List'
import { LoadingSpinner } from 'web_core/assets/spinners'
import { ESBook } from 'web_core/elastic_search'
import { fb } from 'web_core/firebase'
import { useESBooks } from 'web_core/hooks/models/useESBooks'
import { Serialized, deserializeArray, isBlank } from 'web_core/utils'
import { addLocaleProps } from 'web_core/utils/static_props'
import { DefaultLayout } from '~/components/Layout/Default'
import { ContentNavSelector } from '~/components/Utils/ContentNavSelector'
import { FollowingNavSelector } from '~/components/Utils/FollowingNavSelector'
import { Heading } from '~/components/Utils/Heading'
import { SearchFilter } from '~/components/Utils/SearchFilter'
import { useLayoutProperty } from '~/context/layout_property'
import {
  LayoutStaticProps,
  getCategoriesStaticProps,
  getPopularProfilesStaticProps,
} from '~/static_props/layout'
import { bookTimelineBreadcrumb } from '~/utils'

type Props = {
  books: Serialized<ESBook>[]
} & LayoutStaticProps

export const getStaticProps: GetStaticProps<Props> = async ({
  locale = 'ja',
}) =>
  measureGeneration('timeline_books', async (measure) => {
    const { data } = await measure(() =>
      fb.call('search')({
        index: 'books',
        params: { type: 'timeline', mode: 'all', locale: 'ja' },
      })
    )
    if (data.index !== 'books') {
      throw new Error('Unexpected response.')
    }
    const books = data.result
    const result = {
      props: {
        books,
        categories: await measure(() => getCategoriesStaticProps()),
        popularProfiles: await measure(() => getPopularProfilesStaticProps()),
      },
      revalidate: 10,
    }
    return addLocaleProps(result, locale)
  })

const Page: NextPage<Props> = ({
  books: staticBooks,
  categories,
  popularProfiles,
}) => {
  const [categoryId, setCategoryId] = useState<string>()
  const { books, loadMore, isReachingEnd, isLoadingMore } = useESBooks(
    {
      type: 'timeline',
      mode: 'all',
      locale: 'ja',
      category_id:
        !isBlank(categoryId) && categoryId !== '' ? categoryId : undefined,
    },
    deserializeArray(staticBooks, {})
  )
  const { headerHeight } = useLayoutProperty()
  return (
    <DefaultLayout
      head={{
        title: '本のタイムライン',
        description: 'Mathlogに投稿された本を新着順でご覧いただけます。',
      }}
      leftSidebar
      rightSideBar
      categoriesMenu={{ pc: true, sp: true }}
      staticCategories={categories}
      staticPopularProfiles={popularProfiles}
      navLinks={{ pc: true, tablet: true, sp: true, active: 'timeline' }}
    >
      {bookTimelineBreadcrumb.toReactNode()}
      <Heading title="タイムライン" icon={FaHistory} className="mb-0" />
      <div
        className="sticky-top bg-white pt-2 pb-2 mb-2"
        style={{ top: headerHeight }}
      >
        <ContentNavSelector
          active="books"
          link="/timeline/all_[contents]"
          className="mb-2 mb-md-4"
        />
        <div className="d-flex justify-content-between gap-4">
          <FollowingNavSelector
            active="all"
            link="/timeline/[prefix]books"
            className="flex-shrink-0 fs-7 fs-md-6"
          />
          <SearchFilter
            categoryFilter={{ onSelectId: setCategoryId, id: categoryId }}
            className="fs-7 fs-md-6"
          />
        </div>
      </div>
      <InfiniteScroll
        loadMore={async () => {
          if (isLoadingMore === false && isReachingEnd === false)
            await loadMore()
        }}
        hasMore={isReachingEnd === false}
        loader={<LoadingSpinner />}
      >
        {books && <BookList books={books} />}
      </InfiniteScroll>
    </DefaultLayout>
  )
}

export default Page
